import React, {
  useContext,
  useEffect,
  useState,
  useCallback,
  useMemo,
} from "react";
import { UserContext } from "../../../contexts/CurrentUserContext";
import {
  getGeneralDesignAllContracts,
  getGeneralDesignApproval,
  getGeneralDesignApprovalsByApprover,
  getGeneralDesignUserReports,
  getGeneralDesignUsersWithReports,
  getPaymentsProgressApproval,
  getPaymentsProgressApprovalsByApprover,
  getPaymentsProgressGet,
  getPaymentsProgressUserProjects,
  updateGeneralDesignApproverStatus,
  updatePaymentsProgressApproverStatus,
} from "../../../api/paymentsProgressApi";
import Preloader from "../../Preloader/Preloader";
import "./PaymentsProgressApproval.css";

const MONTH_NAMES = [
  "Январь",
  "Февраль",
  "Март",
  "Апрель",
  "Май",
  "Июнь",
  "Июль",
  "Август",
  "Сентябрь",
  "Октябрь",
  "Ноябрь",
  "Декабрь",
];

function formatUserName(user, userIdFallback) {
  const name = [user?.surname, user?.name].filter(Boolean).join(" ").trim();
  if (name) return name;
  if (userIdFallback != null) return `ID ${userIdFallback}`;
  return "—";
}

function formatMoney(value) {
  if (value == null || value === "" || Number.isNaN(Number(value))) return "–";
  return Number(value).toLocaleString("ru-RU", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
}

function getContractSectionId(entity) {
  const raw =
    entity?.rp_gp_labor_effort_id ??
    entity?.contract_sections_id ??
    entity?.contract_section_id ??
    entity?.section_id ??
    entity?.id ??
    entity?.section?.id;
  const id = Number(raw);
  return Number.isFinite(id) && id > 0 ? id : null;
}

function getPeriodKey(item) {
  if (!item?.year || !item?.month) return null;
  return `${item.year}-${String(item.month).padStart(2, "0")}`;
}

function formatPeriodLabel(year, month) {
  return `${MONTH_NAMES[(Number(month) || 1) - 1]} ${year}`;
}

function getCurrentPeriodKey() {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
}

function PaymentsProgressApproval() {
  const { userData } = useContext(UserContext);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [updatingId, setUpdatingId] = useState(null);
  const [expandedIds, setExpandedIds] = useState(() => new Set());
  const [detailByApprovalId, setDetailByApprovalId] = useState({});

  const [gpItems, setGpItems] = useState([]);
  const [gpLoading, setGpLoading] = useState(true);
  const [gpError, setGpError] = useState(null);
  const [gpUpdatingId, setGpUpdatingId] = useState(null);
  const [gpExpandedIds, setGpExpandedIds] = useState(() => new Set());
  const [gpDetailByApprovalId, setGpDetailByApprovalId] = useState({});
  const [gpSectionInfoById, setGpSectionInfoById] = useState({});
  const [selectedPeriod, setSelectedPeriod] = useState(getCurrentPeriodKey);
  const [isEpSectionOpen, setIsEpSectionOpen] = useState(true);
  const [isGpSectionOpen, setIsGpSectionOpen] = useState(true);

  const uniquePeriods = useMemo(() => {
    const keys = new Set();
    [...items, ...gpItems].forEach((item) => {
      const key = getPeriodKey(item);
      if (key) keys.add(key);
    });
    return Array.from(keys).sort((a, b) => b.localeCompare(a));
  }, [items, gpItems]);

  const filteredItems = useMemo(() => {
    if (!selectedPeriod) return items;
    return items.filter((item) => getPeriodKey(item) === selectedPeriod);
  }, [items, selectedPeriod]);

  const filteredGpItems = useMemo(() => {
    if (!selectedPeriod) return gpItems;
    return gpItems.filter((item) => getPeriodKey(item) === selectedPeriod);
  }, [gpItems, selectedPeriod]);

  useEffect(() => {
    if (loading || gpLoading || !selectedPeriod || uniquePeriods.length === 0) {
      return;
    }
    if (!uniquePeriods.includes(selectedPeriod)) {
      setSelectedPeriod(uniquePeriods[0]);
    }
  }, [loading, gpLoading, selectedPeriod, uniquePeriods]);

  useEffect(() => {
    let cancelled = false;
    const fetchApprovals = async () => {
      if (!userData?.id) {
        setLoading(false);
        return;
      }
      setLoading(true);
      setError(null);
      try {
        const approvals = await getPaymentsProgressApprovalsByApprover();

        if (cancelled) return;

        const uniq = new Map();
        // API возвращает либо массив согласований, либо обёртки { approval }; бейдж считает по «плоским» объектам.
        const list = (Array.isArray(approvals) ? approvals : [])
          .map((entry) => (entry?.approval != null ? entry.approval : entry))
          .filter((approval) => approval?.approvers?.length)
          .map((approval) => {
            const myApprover = approval.approvers.find(
              (a) =>
                a.user?.id === userData.id ||
                String(a.user_id) === String(userData.id),
            );
            if (!myApprover) return null;
            return {
              id: approval.id,
              approval,
              myApprover,
              targetUser: approval.user,
              creator: approval.creator,
              month: approval.month,
              year: approval.year,
            };
          })
          .filter(Boolean)
          .filter((item) => {
            if (uniq.has(item.id)) return false;
            uniq.set(item.id, true);
            return true;
          })
          .sort((a, b) => {
            const aKey = `${a.year}-${String(a.month).padStart(2, "0")}`;
            const bKey = `${b.year}-${String(b.month).padStart(2, "0")}`;
            return bKey.localeCompare(aKey);
          });

        setItems(list);
        setExpandedIds(new Set());
        setDetailByApprovalId({});
      } catch (err) {
        if (!cancelled) {
          setError(err?.message || "Ошибка при загрузке согласований выплат");
          setItems([]);
          setExpandedIds(new Set());
          setDetailByApprovalId({});
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchApprovals();
    return () => {
      cancelled = true;
    };
  }, [userData?.id]);

  useEffect(() => {
    let cancelled = false;
    const fetchGpApprovals = async () => {
      if (!userData?.id) {
        setGpLoading(false);
        return;
      }
      setGpLoading(true);
      setGpError(null);
      try {
        const now = new Date();
        const fallbackMonth = now.getMonth() + 1;
        const fallbackYear = now.getFullYear();
        const [approvals, allSections] = await Promise.all([
          getGeneralDesignApprovalsByApprover(),
          getGeneralDesignAllContracts(fallbackMonth, fallbackYear).catch(
            () => [],
          ),
        ]);
        if (cancelled) return;

        const sectionsMap = {};
        (Array.isArray(allSections) ? allSections : []).forEach((row) => {
          const id = getContractSectionId(row);
          if (id == null) return;
          sectionsMap[id] = {
            projectName: row?.project?.name || `Проект ID ${row?.project_id || "—"}`,
            sectionName: row?.section?.section?.name || "—",
            amount: row?.amount ?? row?.section?.amount ?? null,
            labourCost: Number(row?.labour_cost) || null,
          };
        });
        setGpSectionInfoById(sectionsMap);

        const uniq = new Map();
        const list = (Array.isArray(approvals) ? approvals : [])
          .map((entry) => (entry?.approval != null ? entry.approval : entry))
          .filter((approval) => approval?.approvers?.length)
          .map((approval) => {
            const myApprover = approval.approvers.find(
              (a) =>
                a.user?.id === userData.id ||
                String(a.user_id) === String(userData.id),
            );
            if (!myApprover) return null;
            const userId = Number(approval?.user_id ?? approval?.user?.id);
            const csId = getContractSectionId(approval);
            return {
              id: approval.id,
              approval,
              myApprover,
              targetUser: approval.user,
              userId: Number.isFinite(userId) ? userId : null,
              creator: approval.creator,
              month: approval.month,
              year: approval.year,
              contractSectionsId: csId,
              projectName: approval?.project?.name || null,
            };
          })
          .filter(Boolean)
          .filter((item) => {
            if (uniq.has(item.id)) return false;
            uniq.set(item.id, true);
            return true;
          })
          .sort((a, b) => {
            const aKey = `${a.year}-${String(a.month).padStart(2, "0")}`;
            const bKey = `${b.year}-${String(b.month).padStart(2, "0")}`;
            return bKey.localeCompare(aKey);
          });

        setGpItems(list);
        setGpExpandedIds(new Set());
        setGpDetailByApprovalId({});
      } catch (err) {
        if (!cancelled) {
          setGpError(err?.message || "Ошибка при загрузке согласований Генплан РП");
          setGpItems([]);
          setGpExpandedIds(new Set());
          setGpDetailByApprovalId({});
        }
      } finally {
        if (!cancelled) setGpLoading(false);
      }
    };

    fetchGpApprovals();
    return () => {
      cancelled = true;
    };
  }, [userData?.id]);

  const ensurePaymentBreakdown = useCallback(async (item) => {
    const key = item.id;
    let skip = false;
    setDetailByApprovalId((prev) => {
      if (prev[key]?.loaded || prev[key]?.loading) {
        skip = true;
        return prev;
      }
      return {
        ...prev,
        [key]: { loading: true, loaded: false, rows: [], error: null },
      };
    });
    if (skip) return;

    try {
      const userId = Number(item.targetUser?.id ?? item.approval?.user_id);
      if (!userId || Number.isNaN(userId)) {
        throw new Error("Не указан сотрудник для выплаты");
      }

      const projects = await getPaymentsProgressUserProjects(
        item.month,
        item.year,
        userId,
      );
      const projectList = Array.isArray(projects) ? projects : [];

      const rows = await Promise.all(
        projectList.map(async (project) => {
          const statsRaw = await getPaymentsProgressGet(
            item.month,
            item.year,
            userId,
            project.id,
          );
          const stats = Array.isArray(statsRaw) ? statsRaw : [];
          let total = 0;
          for (const stat of stats) {
            const corr = stat.correction ?? {};
            const sum =
              corr.completion_sum_after_correction ??
              stat.to_be_payed_by_employee ??
              0;
            total += Number(sum) || 0;
          }
          return {
            projectId: project.id,
            projectName: project.name || `Проект ${project.id}`,
            total,
          };
        }),
      );

      const sorted = [...rows].sort((a, b) => b.total - a.total);

      setDetailByApprovalId((prev) => ({
        ...prev,
        [key]: { loading: false, loaded: true, rows: sorted, error: null },
      }));
    } catch (err) {
      setDetailByApprovalId((prev) => ({
        ...prev,
        [key]: {
          loading: false,
          loaded: false,
          rows: [],
          error: err?.message || "Не удалось загрузить суммы по проектам",
        },
      }));
    }
  }, []);

  const toggleRowExpanded = (item) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      const closing = next.has(item.id);
      if (closing) {
        next.delete(item.id);
      } else {
        next.add(item.id);
        Promise.resolve().then(() => ensurePaymentBreakdown(item));
      }
      return next;
    });
  };

  const ensureGpBreakdown = useCallback(async (item) => {
    const key = item.id;
    let skip = false;
    setGpDetailByApprovalId((prev) => {
      if (prev[key]?.loaded || prev[key]?.loading) {
        skip = true;
        return prev;
      }
      return {
        ...prev,
        [key]: { loading: true, loaded: false, rows: [], error: null },
      };
    });
    if (skip) return;

    try {
      const userId = Number(item.userId ?? item.targetUser?.id ?? item.approval?.user_id);
      if (!userId || Number.isNaN(userId)) {
        throw new Error("Не указан сотрудник для выплаты Генплан РП");
      }

      const contracts = await getGeneralDesignAllContracts(item.month, item.year);
      const contractList = Array.isArray(contracts) ? contracts : [];

      const rows = (
        await Promise.all(
          contractList.map(async (row) => {
            const csId = getContractSectionId(row);
            if (csId == null) return null;
            try {
              const users = await getGeneralDesignUsersWithReports(
                item.month,
                item.year,
                csId,
              );
              const hasUser = (Array.isArray(users) ? users : []).some(
                (user) => Number(user.id) === userId,
              );
              if (!hasUser) return null;
              const data = await getGeneralDesignUserReports(
                item.month,
                item.year,
                csId,
                userId,
              );
              const correction =
                data && !Array.isArray(data) ? data.correction || {} : {};
              const total =
                Number(correction.completion_sum_after_correction) || 0;
              const projectName =
                row?.project?.name ||
                item.projectName ||
                `Проект ID ${row?.project_id || csId}`;
              const sectionName = row?.section?.section?.name;
              return {
                projectId: csId,
                projectName:
                  sectionName && sectionName !== "—"
                    ? `${projectName} — ${sectionName}`
                    : projectName,
                total,
              };
            } catch {
              return null;
            }
          }),
        )
      ).filter(Boolean);

      const sorted = [...rows].sort((a, b) => b.total - a.total);

      setGpDetailByApprovalId((prev) => ({
        ...prev,
        [key]: { loading: false, loaded: true, rows: sorted, error: null },
      }));
    } catch (err) {
      setGpDetailByApprovalId((prev) => ({
        ...prev,
        [key]: {
          loading: false,
          loaded: false,
          rows: [],
          error: err?.message || "Не удалось загрузить суммы по проектам",
        },
      }));
    }
  }, []);

  const toggleGpRowExpanded = (item) => {
    setGpExpandedIds((prev) => {
      const next = new Set(prev);
      const closing = next.has(item.id);
      if (closing) {
        next.delete(item.id);
      } else {
        next.add(item.id);
        Promise.resolve().then(() => ensureGpBreakdown(item));
      }
      return next;
    });
  };

  const getStatusText = (status) => {
    const s = String(status || "").toLowerCase();
    if (s === "approved" || s === "согласован") return "Согласован";
    if (s === "rejected" || s === "отклонен") return "Отклонен";
    return "На рассмотрении";
  };

  const getStatusClassName = (status) => {
    const s = String(status || "").toLowerCase();
    if (s === "approved" || s === "согласован") {
      return "pp-approval__approver-status--approved";
    }
    if (s === "rejected" || s === "отклонен") {
      return "pp-approval__approver-status--rejected";
    }
    return "pp-approval__approver-status--pending";
  };

  const isApproverPending = (status) => {
    const s = String(status || "").toLowerCase();
    return (
      !s || s === "in_processing" || s === "pending" || s === "на рассмотрении"
    );
  };

  const handleSetStatus = async (item, status) => {
    if (!item.myApprover?.id) return;
    setUpdatingId(item.id);
    try {
      await updatePaymentsProgressApproverStatus(item.myApprover.id, status);
      const refreshed = await getPaymentsProgressApproval(
        item.year,
        item.month,
        item.targetUser?.id || item.approval?.user_id,
      );
      const nextMyApprover =
        refreshed?.approvers?.find(
          (a) =>
            a.user?.id === userData?.id ||
            String(a.user_id) === String(userData?.id),
        ) || item.myApprover;
      setItems((prev) =>
        prev.map((i) =>
          i.id === item.id
            ? {
                ...i,
                approval: refreshed || i.approval,
                myApprover: nextMyApprover,
              }
            : i,
        ),
      );
    } catch (err) {
      alert(err?.message || "Ошибка обновления статуса");
    } finally {
      setUpdatingId(null);
    }
  };

  const handleGpSetStatus = async (item, status) => {
    if (!item.myApprover?.id) return;
    setGpUpdatingId(item.id);
    try {
      const updatedApprover = await updateGeneralDesignApproverStatus(
        item.myApprover.id,
        status,
      );
      const refreshed = await getGeneralDesignApproval(
        item.year,
        item.month,
        item.userId ?? item.targetUser?.id ?? item.approval?.user_id,
      );
      const nextMyApprover =
        refreshed?.approvers?.find(
          (a) =>
            a.user?.id === userData?.id ||
            String(a.user_id) === String(userData?.id),
        ) ||
        (updatedApprover?.id ? updatedApprover : null) ||
        item.myApprover;
      setGpItems((prev) =>
        prev.map((i) =>
          i.id === item.id
            ? {
                ...i,
                approval: refreshed || i.approval,
                myApprover: nextMyApprover,
              }
            : i,
        ),
      );
    } catch (err) {
      alert(err?.message || "Ошибка обновления статуса Генплан РП");
    } finally {
      setGpUpdatingId(null);
    }
  };

  if (loading && gpLoading) return <Preloader />;

  const hasAnyItems = items.length > 0 || gpItems.length > 0;
  const showPeriodFilter = !loading && !gpLoading && hasAnyItems;

  return (
    <div className="pp-approval">
      {showPeriodFilter && (
        <div className="pp-approval__filters">
          <label className="pp-approval__filter-label" htmlFor="pp-period-filter">
            Период:
          </label>
          <select
            id="pp-period-filter"
            className="pp-approval__date-select"
            value={selectedPeriod}
            onChange={(e) => setSelectedPeriod(e.target.value)}
          >
            <option value="">Все периоды</option>
            {uniquePeriods.map((key) => {
              const [year, month] = key.split("-");
              return (
                <option key={key} value={key}>
                  {formatPeriodLabel(year, month)}
                </option>
              );
            })}
          </select>
        </div>
      )}

      <section className="pp-approval__section">
        <button
          type="button"
          className="pp-approval__section-title"
          onClick={() => setIsEpSectionOpen((open) => !open)}
          aria-expanded={isEpSectionOpen}
        >
          <span
            className={`pp-approval__chevron ${isEpSectionOpen ? "pp-approval__chevron--open" : ""}`}
            aria-hidden
          >
            ›
          </span>
          Согласование выплат ЭП
        </button>

        {isEpSectionOpen && (
          <>
      {loading && (
        <div className="pp-approval__loading">
          <Preloader />
        </div>
      )}

      {!loading && error && <div className="pp-approval__error">{error}</div>}

      {!loading && !error && filteredItems.length === 0 && (
        <div className="pp-approval__empty">
          {items.length === 0
            ? "Нет согласований выплат ЭП на ваше утверждение"
            : "Нет согласований выплат ЭП за выбранный период"}
        </div>
      )}

      {!loading && !error && filteredItems.length > 0 && (
        <div className="pp-approval__table-wrap">
          <p className="pp-approval__hint">
            Нажмите на строку, чтобы посмотреть суммы к выплате по проектам (как
            на прогрессе выплат: с учётом корректировки руководителя).
          </p>
          <table className="pp-approval__table">
            <thead>
              <tr>
                <th>Сотрудник</th>
                <th>Инициатор</th>
                <th>Период</th>
                <th>Согласующие</th>
                <th>Действия</th>
              </tr>
            </thead>
            <tbody>
              {filteredItems.map((item) => {
                const status = item.myApprover?.status;
                const isPending = isApproverPending(status);
                const userName =
                  [item.targetUser?.surname, item.targetUser?.name]
                    .filter(Boolean)
                    .join(" ")
                    .trim() ||
                  `ID ${item.targetUser?.id || item.approval?.user_id}`;
                const creatorName =
                  [item.creator?.surname, item.creator?.name]
                    .filter(Boolean)
                    .join(" ")
                    .trim() || "—";
                const period = `${MONTH_NAMES[(item.month || 1) - 1]} ${item.year || ""}`;
                const isUpdating = updatingId === item.id;
                const approversList = item.approval?.approvers || [];
                const expanded = expandedIds.has(item.id);
                const breakdown = detailByApprovalId[item.id];
                const breakdownRows = breakdown?.rows ?? [];
                const grandTotal = breakdownRows.reduce(
                  (acc, r) => acc + (Number(r.total) || 0),
                  0,
                );

                return (
                  <React.Fragment key={item.id}>
                    <tr
                      className="pp-approval__row-expandable"
                      onClick={() => toggleRowExpanded(item)}
                      title="Показать или скрыть суммы по проектам"
                    >
                      <td>
                        <span
                          className={`pp-approval__chevron ${expanded ? "pp-approval__chevron--open" : ""}`}
                          aria-hidden
                        >
                          ›
                        </span>
                        {userName}
                      </td>
                      <td>{creatorName}</td>
                      <td>{period}</td>
                      <td className="pp-approval__cell--approvers">
                        <ul className="pp-approval__approvers-list">
                          {approversList.map((approver) => (
                            <li
                              key={
                                approver.id ??
                                `${approver.user_id}-${approver.approval_id}`
                              }
                            >
                              <span className="pp-approval__approver-name">
                                {formatUserName(
                                  approver.user,
                                  approver.user_id,
                                )}
                              </span>
                              <span
                                className={`pp-approval__approver-status ${getStatusClassName(approver.status)}`}
                              >
                                {getStatusText(approver.status)}
                              </span>
                            </li>
                          ))}
                        </ul>
                      </td>
                      <td onClick={(e) => e.stopPropagation()}>
                        <div className="pp-approval__actions">
                          {isPending && (
                            <>
                              <button
                                type="button"
                                className="pp-approval__btn-approve"
                                onClick={() =>
                                  handleSetStatus(item, "approved")
                                }
                                disabled={isUpdating}
                              >
                                {isUpdating ? "..." : "Согласовать"}
                              </button>
                              <button
                                type="button"
                                className="pp-approval__btn-reject"
                                onClick={() =>
                                  handleSetStatus(item, "rejected")
                                }
                                disabled={isUpdating}
                              >
                                Отклонить
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                    {expanded && (
                      <tr className="pp-approval__breakdown-row">
                        <td colSpan={5}>
                          <div className="pp-approval__breakdown">
                            {breakdown?.loading && (
                              <div className="pp-approval__breakdown-loading">
                                <Preloader />
                              </div>
                            )}
                            {!breakdown?.loading && breakdown?.error && (
                              <div className="pp-approval__breakdown-error">
                                {breakdown.error}
                              </div>
                            )}
                            {!breakdown?.loading &&
                              !breakdown?.error &&
                              breakdownRows.length === 0 && (
                                <div className="pp-approval__breakdown-empty">
                                  Нет проектов с начислениями за этот период.
                                </div>
                              )}
                            {!breakdown?.loading &&
                              !breakdown?.error &&
                              breakdownRows.length > 0 && (
                                <table className="pp-approval__breakdown-table">
                                  <thead>
                                    <tr>
                                      <th>Проект</th>
                                      <th className="pp-approval__money">
                                        К выплате по проекту (итого)
                                      </th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {breakdownRows.map((row) => (
                                      <tr key={row.projectId}>
                                        <td>{row.projectName}</td>
                                        <td className="pp-approval__money">
                                          ₸ {formatMoney(row.total)}
                                        </td>
                                      </tr>
                                    ))}
                                    <tr className="pp-approval__breakdown-total">
                                      <td>Всего по сотруднику за период</td>
                                      <td className="pp-approval__money">
                                        ₸ {formatMoney(grandTotal)}
                                      </td>
                                    </tr>
                                  </tbody>
                                </table>
                              )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
          </>
        )}
      </section>

      <section className="pp-approval__section pp-approval__section--mt">
        <button
          type="button"
          className="pp-approval__section-title"
          onClick={() => setIsGpSectionOpen((open) => !open)}
          aria-expanded={isGpSectionOpen}
        >
          <span
            className={`pp-approval__chevron ${isGpSectionOpen ? "pp-approval__chevron--open" : ""}`}
            aria-hidden
          >
            ›
          </span>
          Согласование выплат Генплан РП
        </button>

        {isGpSectionOpen && (
          <>
      {gpLoading && (
        <div className="pp-approval__loading">
          <Preloader />
        </div>
      )}

      {!gpLoading && gpError && (
        <div className="pp-approval__error">{gpError}</div>
      )}

      {!gpLoading && !gpError && filteredGpItems.length === 0 && (
        <div className="pp-approval__empty">
          {gpItems.length === 0
            ? "Нет согласований выплат Генплан РП на ваше утверждение"
            : "Нет согласований выплат Генплан РП за выбранный период"}
        </div>
      )}

      {!gpLoading && !gpError && filteredGpItems.length > 0 && (
        <div className="pp-approval__table-wrap">
          <p className="pp-approval__hint">
            Нажмите на строку, чтобы посмотреть суммы к выплате по проектам
            сотрудника (с учётом корректировки руководителя).
          </p>
          <table className="pp-approval__table">
            <thead>
              <tr>
                <th>Сотрудник</th>
                <th>Инициатор</th>
                <th>Период</th>
                <th>Согласующие</th>
                <th>Действия</th>
              </tr>
            </thead>
            <tbody>
              {filteredGpItems.map((item) => {
                const status = item.myApprover?.status;
                const isPending = isApproverPending(status);
                const employeeName = formatUserName(
                  item.targetUser,
                  item.userId ?? item.approval?.user_id,
                );
                const sectionInfo =
                  gpSectionInfoById[Number(item.contractSectionsId)] ||
                  gpSectionInfoById[String(item.contractSectionsId)];
                const projectLabel = item.projectName || sectionInfo?.projectName || null;
                const fallbackSectionLabel =
                  projectLabel && sectionInfo?.sectionName && sectionInfo.sectionName !== "—"
                    ? `${projectLabel} — ${sectionInfo.sectionName}`
                    : projectLabel ||
                      (item.contractSectionsId != null
                        ? `Раздел ID ${item.contractSectionsId}`
                        : null);
                const rowLabel =
                  employeeName && employeeName !== "—"
                    ? employeeName
                    : fallbackSectionLabel || "—";
                const creatorName =
                  [item.creator?.surname, item.creator?.name]
                    .filter(Boolean)
                    .join(" ")
                    .trim() || "—";
                const period = `${MONTH_NAMES[(item.month || 1) - 1]} ${item.year || ""}`;
                const isUpdating = gpUpdatingId === item.id;
                const approversList = item.approval?.approvers || [];
                const expanded = gpExpandedIds.has(item.id);
                const breakdown = gpDetailByApprovalId[item.id];
                const breakdownRows = breakdown?.rows ?? [];
                const grandTotal = breakdownRows.reduce(
                  (acc, r) => acc + (Number(r.total) || 0),
                  0,
                );

                return (
                  <React.Fragment key={item.id}>
                    <tr
                      className="pp-approval__row-expandable"
                      onClick={() => toggleGpRowExpanded(item)}
                      title="Показать или скрыть суммы по проектам"
                    >
                      <td>
                        <span
                          className={`pp-approval__chevron ${expanded ? "pp-approval__chevron--open" : ""}`}
                          aria-hidden
                        >
                          ›
                        </span>
                        {rowLabel}
                      </td>
                      <td>{creatorName}</td>
                      <td>{period}</td>
                      <td className="pp-approval__cell--approvers">
                        <ul className="pp-approval__approvers-list">
                          {approversList.map((approver) => (
                            <li
                              key={
                                approver.id ??
                                `${approver.user_id}-${approver.approval_id}`
                              }
                            >
                              <span className="pp-approval__approver-name">
                                {formatUserName(
                                  approver.user,
                                  approver.user_id,
                                )}
                              </span>
                              <span
                                className={`pp-approval__approver-status ${getStatusClassName(approver.status)}`}
                              >
                                {getStatusText(approver.status)}
                              </span>
                            </li>
                          ))}
                        </ul>
                      </td>
                      <td onClick={(e) => e.stopPropagation()}>
                        <div className="pp-approval__actions">
                          {isPending && (
                            <>
                              <button
                                type="button"
                                className="pp-approval__btn-approve"
                                onClick={() =>
                                  handleGpSetStatus(item, "approved")
                                }
                                disabled={isUpdating}
                              >
                                {isUpdating ? "..." : "Согласовать"}
                              </button>
                              <button
                                type="button"
                                className="pp-approval__btn-reject"
                                onClick={() =>
                                  handleGpSetStatus(item, "rejected")
                                }
                                disabled={isUpdating}
                              >
                                Отклонить
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                    {expanded && (
                      <tr className="pp-approval__breakdown-row">
                        <td colSpan={5}>
                          <div className="pp-approval__breakdown">
                            {breakdown?.loading && (
                              <div className="pp-approval__breakdown-loading">
                                <Preloader />
                              </div>
                            )}
                            {!breakdown?.loading && breakdown?.error && (
                              <div className="pp-approval__breakdown-error">
                                {breakdown.error}
                              </div>
                            )}
                            {!breakdown?.loading &&
                              !breakdown?.error &&
                              breakdownRows.length === 0 && (
                                <div className="pp-approval__breakdown-empty">
                                  Нет проектов с начислениями по этому
                                  сотруднику за период.
                                </div>
                              )}
                            {!breakdown?.loading &&
                              !breakdown?.error &&
                              breakdownRows.length > 0 && (
                                <table className="pp-approval__breakdown-table">
                                  <thead>
                                    <tr>
                                      <th>Проект</th>
                                      <th className="pp-approval__money">
                                        К выплате по корректировке
                                      </th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {breakdownRows.map((row) => (
                                      <tr key={row.projectId}>
                                        <td>{row.projectName}</td>
                                        <td className="pp-approval__money">
                                          ₸ {formatMoney(row.total)}
                                        </td>
                                      </tr>
                                    ))}
                                    <tr className="pp-approval__breakdown-total">
                                      <td>Всего по сотруднику за период</td>
                                      <td className="pp-approval__money">
                                        ₸ {formatMoney(grandTotal)}
                                      </td>
                                    </tr>
                                  </tbody>
                                </table>
                              )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
          </>
        )}
      </section>
    </div>
  );
}

export default PaymentsProgressApproval;
